/**
 * Lexical Rich Text Renderer
 *
 * Converts Lexical editor JSON content from Payload CMS to HTML strings.
 * Used with innerHTML for server-side rendering and client-side hydration.
 */

import type { LexicalRichText } from "~/types/lexicalrichtext";
import { logger } from "~/lib/logger/logger";
import { resolveMediaUrl } from "~/utils/media";
import { escapeHtml, sanitizeUrl, escapeAttribute, sanitizeHtml } from "~/utils/htmlSecurity";
import { renderSocialEmbed } from "./socialEmbedRenderer";

/**
 * Lexical text format bitflags
 * @see https://lexical.dev/docs/concepts/serialization
 */
const TEXT_FORMAT = {
  BOLD: 1,
  ITALIC: 2,
  STRIKETHROUGH: 4,
  UNDERLINE: 8,
  CODE: 16,
  SUBSCRIPT: 32,
  SUPERSCRIPT: 64,
} as const;

/**
 * Style classes for rich text elements
 *
 * CSS Module class names can be passed to apply scoped styles
 * to each HTML element generated by renderLexicalContent.
 */
export interface RichTextStyles {
  // Typography
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  h5?: string;
  h6?: string;
  p?: string;
  strong?: string;
  em?: string;
  u?: string;
  s?: string;
  code?: string;
  sub?: string;
  sup?: string;
  // Lists
  ul?: string;
  ol?: string;
  li?: string;
  // Links
  a?: string;
  // Media
  img?: string;
  imgSmall?: string;
  imgMedium?: string;
  imgLarge?: string;
  imgFull?: string;
  figure?: string;
  figcaption?: string;
  // Blocks
  blockquote?: string;
  pre?: string;
  // Line break
  br?: string;
  // Social embeds
  socialEmbed?: string;
  socialEmbedLink?: string;
  // Consent overlay (for GDPR)
  consentWrapper?: string;
  consentOverlay?: string;
  consentContent?: string;
}

/**
 * Renders Lexical rich text content to HTML string
 *
 * Handles the following node types:
 * - text: Plain text nodes with formatting (bold, italic, etc.)
 * - paragraph: Paragraph blocks
 * - heading: H1-H6 heading blocks
 * - link: Anchor links (internal and external)
 * - autolink: Automatic links (URLs converted to links)
 * - linebreak: Line break elements
 * - list: Ordered and unordered lists
 * - listitem: List items
 * - quote: Blockquote elements
 * - code: Code blocks
 * - upload: Inline images/files from media library
 * - socialEmbed: Social media embeds (Instagram, Twitter, YouTube, TikTok, etc.)
 * - root: Root container node
 *
 * @param richText - Lexical JSON structure from CMS
 * @param styles - Optional CSS Module classes to apply to each element
 * @returns HTML string ready for innerHTML
 */
export function renderLexicalContent(
  richText: LexicalRichText | null | undefined,
  styles?: RichTextStyles
): string {
  if (!richText || !richText.root) return "";

  // Helper to build class attribute string
  const cls = (className?: string) => (className ? ` class="${className}"` : "");

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CMS nodes have many dynamic shapes beyond our typed subset
    const renderNode = (node: Record<string, any>): string => {
      // Text Node
      if (node.type === "text") {
        // Escape HTML to prevent XSS
        let text = escapeHtml(node.text || "");

        // Apply text formatting with optional styles
        if (node.format) {
          if (node.format & TEXT_FORMAT.BOLD)
            text = `<strong${cls(styles?.strong)}>${text}</strong>`;
          if (node.format & TEXT_FORMAT.ITALIC) text = `<em${cls(styles?.em)}>${text}</em>`;
          if (node.format & TEXT_FORMAT.STRIKETHROUGH) text = `<s${cls(styles?.s)}>${text}</s>`;
          if (node.format & TEXT_FORMAT.UNDERLINE) text = `<u${cls(styles?.u)}>${text}</u>`;
          if (node.format & TEXT_FORMAT.CODE) text = `<code${cls(styles?.code)}>${text}</code>`;
          if (node.format & TEXT_FORMAT.SUBSCRIPT) text = `<sub${cls(styles?.sub)}>${text}</sub>`;
          if (node.format & TEXT_FORMAT.SUPERSCRIPT) text = `<sup${cls(styles?.sup)}>${text}</sup>`;
        }

        return text;
      }

      // Paragraph Node
      if (node.type === "paragraph") {
        const children = node.children?.map(renderNode).join("") || "";
        return `<p${cls(styles?.p)}>${children}</p>`;
      }

      // Heading Node
      if (node.type === "heading") {
        const children = node.children?.map(renderNode).join("") || "";
        const tag = node.tag || "h2";
        const headingClass = styles?.[tag as keyof RichTextStyles] as string | undefined;
        return `<${tag}${cls(headingClass)}>${children}</${tag}>`;
      }

      // Link Node
      if (node.type === "link") {
        const children = node.children?.map(renderNode).join("") || "";
        let url = "#";

        // Handle different link structures from Lexical
        if (node.fields?.doc) {
          const doc = node.fields.doc;
          // Handle populated doc (object with slug)
          if (typeof doc === "object" && doc !== null && doc.slug) {
            url = doc.slug === "home" ? "/" : `/${doc.slug}`;
          }
          // Handle doc with value property
          else if (typeof doc.value === "object" && doc.value !== null && doc.value?.slug) {
            url = doc.value.slug === "home" ? "/" : `/${doc.value.slug}`;
          }
        } else if (node.fields?.url) {
          url = node.fields.url;
        } else if (node.url) {
          url = node.url;
        }

        // Sanitize URL to prevent javascript: XSS attacks
        const safeUrl = escapeAttribute(sanitizeUrl(url));
        const newTab = node.fields?.newTab || node.newTab;
        const target = newTab ? ' target="_blank" rel="noopener noreferrer"' : "";
        return `<a href="${safeUrl}"${cls(styles?.a)}${target}>${children}</a>`;
      }

      // AutoLink Node (automatic links from URLs)
      // Note: Lexical stores the URL in node.fields.url (not node.url)
      if (node.type === "autolink") {
        const children = node.children?.map(renderNode).join("") || "";
        // Sanitize URL to prevent javascript: XSS attacks
        const safeUrl = escapeAttribute(sanitizeUrl(node.fields?.url || node.url || "#"));
        return `<a href="${safeUrl}"${cls(styles?.a)} target="_blank" rel="noopener noreferrer">${children}</a>`;
      }

      // Line Break Node
      if (node.type === "linebreak") {
        return styles?.br ? `<br class="${styles.br}" />` : "<br />";
      }

      // List Node
      if (node.type === "list") {
        const children = node.children?.map(renderNode).join("") || "";
        const tag = node.listType === "number" ? "ol" : "ul";
        const listClass = tag === "ol" ? styles?.ol : styles?.ul;
        return `<${tag}${cls(listClass)}>${children}</${tag}>`;
      }

      // List Item Node
      if (node.type === "listitem") {
        const children = node.children?.map(renderNode).join("") || "";
        return `<li${cls(styles?.li)}>${children}</li>`;
      }

      // Quote Node
      if (node.type === "quote") {
        const children = node.children?.map(renderNode).join("") || "";
        return `<blockquote${cls(styles?.blockquote)}>${children}</blockquote>`;
      }

      // Code Block Node
      if (node.type === "code") {
        // Escape code content to prevent XSS
        const code = escapeHtml(
          node.children
            ?.map((child: Record<string, unknown>) => (child.text as string) || "")
            .join("") || ""
        );
        const language = escapeAttribute(node.language || "");
        const codeClass = styles?.code
          ? `${styles.code} language-${language}`
          : `language-${language}`;
        return `<pre${cls(styles?.pre)}><code class="${codeClass}">${code}</code></pre>`;
      }

      // Social Embed Node
      if (node.type === "socialEmbed") {
        // Data can be in node.data (our format) or node.fields (Payload format)
        const { url, platform } = node.data || node.fields || {};
        if (!url) return "";
        return renderSocialEmbed(url, platform);
      }

      // Upload Node (inline images/files in rich text)
      if (node.type === "upload") {
        const media = node.value;
        if (!media || !media.url) return "";

        const imageUrl = resolveMediaUrl(media.url);
        if (!imageUrl) return "";

        const alt = escapeAttribute(media.alt || media.filename || "");
        const width = media.width ? `width="${media.width}"` : "";
        const height = media.height ? `height="${media.height}"` : "";

        // Get size from node.fields (Payload stores custom upload fields here)
        const size = node.fields?.size || "default";

        // Get size-specific class
        const getSizeClass = () => {
          switch (size) {
            case "small":
              return styles?.imgSmall;
            case "medium":
              return styles?.imgMedium;
            case "large":
              return styles?.imgLarge;
            case "full":
              return styles?.imgFull;
            default:
              return undefined;
          }
        };

        // Combine base img class with size class
        const imgClasses = [styles?.img, getSizeClass()].filter(Boolean).join(" ");
        const imgClassAttr = imgClasses ? ` class="${imgClasses}"` : "";

        // Check if there's a caption (caption is a Lexical rich text structure)
        const hasCaption = media.caption?.root?.children?.length > 0;

        if (hasCaption) {
          // Render as figure with figcaption
          const captionHtml = renderNode(media.caption.root);
          return `<figure${cls(styles?.figure)}><img${imgClassAttr} src="${escapeAttribute(imageUrl)}" alt="${alt}" ${width} ${height} loading="lazy" /><figcaption${cls(styles?.figcaption)}>${captionHtml}</figcaption></figure>`;
        }

        // Render as standalone image
        return `<img${imgClassAttr} src="${escapeAttribute(imageUrl)}" alt="${alt}" ${width} ${height} loading="lazy" />`;
      }

      // Root Node
      if (node.type === "root") {
        return node.children?.map(renderNode).join("") || "";
      }

      // Unknown node type - log warning and skip
      logger.warn("[Lexical] Unknown node type:", node.type);
      return "";
    };

    // Final sanitization pass with DOMPurify for defense in depth
    return sanitizeHtml(renderNode(richText.root));
  } catch (error) {
    logger.error("[Lexical] Error rendering content:", error);
    return "<p>Inhalt konnte nicht dargestellt werden.</p>";
  }
}
